import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../config/api';
import { auth } from '../firebase/config';
import './CoverSheetPreviewPage.css';

// Local getFirebaseToken wrapper — matches convention from DisclosureTab + CLAUDE.md.
const getFirebaseToken = () => new Promise((resolve, reject) => {
  if (auth.currentUser) {
    auth.currentUser.getIdToken(true).then(resolve).catch(reject);
    return;
  }
  const timeoutId = setTimeout(() => {
    unsubscribe();
    reject(new Error('Firebase auth timeout'));
  }, 10000);
  const unsubscribe = auth.onAuthStateChanged(async (user) => {
    clearTimeout(timeoutId);
    unsubscribe();
    if (user) {
      try {
        resolve(await user.getIdToken(true));
      } catch (e) {
        reject(e);
      }
    } else {
      reject(new Error('No authenticated user'));
    }
  });
});

/**
 * Agent/TC preview of the disclosure folder's cover sheet, rendered as HTML
 * from the server-composed JSON (folderCoverSheetContent). Keeps the agent
 * inside the app with "Back" + "Download PDF" chrome; the PDF endpoint stays
 * the download source of truth. Matches design mockup Screen 2.
 */
const CoverSheetPreviewPage = () => {
  const { id: transactionId } = useParams();
  const navigate = useNavigate();

  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  // Property photo is served as a blob behind Bearer auth, so we fetch it into
  // an object URL rather than putting the raw endpoint in <img src> (which would
  // bypass the Authorization header). Null until resolved or if backend sent
  // propertyPhotoUrl=null — in the latter case the agent sees an "Add photo"
  // CTA at the same spot (upload affordance belongs AT the render site; see
  // feedback_cover_sheet_photo_upload_lives_in_preview.md).
  const [photoObjectUrl, setPhotoObjectUrl] = useState(null);
  // Bumped on every successful upload so the bytes-fetch effect reruns even
  // when propertyPhotoUrl is the same string before and after (replace flow),
  // AND is passed as a ?v= query so the backend's Cache-Control: max-age=300
  // on GET /cover-sheet-photo doesn't let the browser serve the stale bytes.
  const [photoVersion, setPhotoVersion] = useState(0);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isRemovingPhoto, setIsRemovingPhoto] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  const [confirmRemove, setConfirmRemove] = useState(false);
  const photoInputRef = useRef(null);

  const showNotification = useCallback((type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification((n) => ({ ...n, show: false })), 3500);
  }, []);

  const fetchContent = useCallback(async () => {
    if (!transactionId) return;
    setIsLoading(true);
    setErrorMsg(null);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(
        API_ENDPOINTS.folderCoverSheetContent(transactionId, 'disclosure'),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContent(res.data || null);
    } catch (err) {
      console.error('[CoverSheetPreview] load failed', err);
      setErrorMsg(
        err?.response?.status === 403
          ? 'You do not have access to this cover sheet.'
          : 'Could not load the cover sheet. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [transactionId]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  // When content arrives with a non-null propertyPhotoUrl, fetch the auth-gated
  // bytes once and expose them as an object URL. On unmount (or on refetch
  // producing a new URL) the previous blob is revoked to avoid a memory leak.
  useEffect(() => {
    const relativeUrl = content?.propertyPhotoUrl;
    if (!transactionId || !relativeUrl) {
      setPhotoObjectUrl(null);
      return undefined;
    }
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(
          API_ENDPOINTS.coverSheetPhoto(transactionId),
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
            // Cache-buster — see photoVersion comment at the top of this component.
            params: photoVersion > 0 ? { v: photoVersion } : undefined,
          }
        );
        if (cancelled) return;
        createdUrl = URL.createObjectURL(res.data);
        setPhotoObjectUrl(createdUrl);
      } catch (err) {
        // 404 (orphan path) → fall back to the photo-less layout silently; a
        // visible error here would be noisy for a case the layout already handles.
        if (err?.response?.status !== 404) {
          console.warn('[CoverSheetPreview] photo fetch failed', err);
        }
        if (!cancelled) setPhotoObjectUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [content?.propertyPhotoUrl, transactionId, photoVersion]);

  // ------ Photo upload / change / remove (agent-only UX; backend 403s viewers) ------

  const handlePhotoButtonClick = () => {
    photoInputRef.current?.click();
  };

  const handlePhotoFileChange = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (!file || !transactionId) return;
    // Client-side guardrails match CoverSheetServiceImpl.MAX_PHOTO_BYTES +
    // ALLOWED_INBOUND_TYPES so the user gets instant feedback instead of a 400.
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      showNotification('error', 'Please choose a JPEG or PNG image.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showNotification('error', 'Photo must be 10MB or smaller.');
      return;
    }
    setIsUploadingPhoto(true);
    try {
      const token = await getFirebaseToken();
      const formData = new FormData();
      formData.append('file', file, file.name);
      // axios.put directly (not apiClient) so the multipart Content-Type boundary
      // header isn't rewritten by the shared interceptor — same reason document
      // upload uses axios.post directly.
      await axios.put(
        API_ENDPOINTS.coverSheetPhoto(transactionId),
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Bump BEFORE fetchContent so the bytes-fetch effect sees a new dep even
      // if propertyPhotoUrl is the same string (replace flow). Without this,
      // changing an existing photo would require a page refresh.
      setPhotoVersion((v) => v + 1);
      // Refetch content so propertyPhotoUrl flips null → populated on the
      // first upload (otherwise the <img> branch wouldn't render at all).
      await fetchContent();
      showNotification('success', 'Photo updated.');
    } catch (err) {
      console.error('[CoverSheetPreview] photo upload failed', err);
      const msg = err?.response?.data?.message || 'Could not upload photo. Please try again.';
      showNotification('error', msg);
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleRemovePhotoConfirmed = async () => {
    setConfirmRemove(false);
    if (!transactionId) return;
    setIsRemovingPhoto(true);
    try {
      const token = await getFirebaseToken();
      await apiClient.delete(
        API_ENDPOINTS.coverSheetPhoto(transactionId),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchContent();
      showNotification('success', 'Photo removed.');
    } catch (err) {
      console.error('[CoverSheetPreview] photo remove failed', err);
      showNotification('error', 'Could not remove photo. Please try again.');
    } finally {
      setIsRemovingPhoto(false);
    }
  };

  const handleBack = () => {
    // Always land back on the Disclosures tab specifically — the plain navigate(-1)
    // bounced to the browser's prior history entry which re-mounts the detail page
    // with the default (Timeline) tab, discarding the "I was on Disclosures" context.
    // location.state.activeTab is already honored by TransactionDetailPage's useState
    // initializer (see TransactionDetailPage.js:97).
    navigate(`/transaction/${transactionId}`, { state: { activeTab: 'disclosure' } });
  };

  const handleDownload = async () => {
    if (!transactionId) return;
    try {
      const token = await getFirebaseToken();
      const res = await axios.get(
        API_ENDPOINTS.folderCoverSheetPdf(transactionId, 'disclosure'),
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cover-sheet.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('[CoverSheetPreview] download failed', err);
    }
  };

  const agent = content?.listingAgent || {};
  const contactLine = [agent.phone, agent.email].filter(Boolean).join(' · ');

  return (
    <div className="cover-sheet-preview-page">
      <div className="cover-sheet-preview-chrome">
        <button type="button" className="cover-sheet-preview-back" onClick={handleBack}>
          ← Back to Disclosures
        </button>
        <div className="cover-sheet-preview-actions">
          <button
            type="button"
            className="cover-sheet-preview-download"
            onClick={handleDownload}
            disabled={isLoading || !!errorMsg}
          >
            Download PDF
          </button>
        </div>
      </div>

      {errorMsg ? (
        <div className="cover-sheet-preview-error-card">{errorMsg}</div>
      ) : isLoading ? (
        <div className="cover-sheet-preview-loading-card">Loading preview…</div>
      ) : content ? (
        <article className="cover-sheet-render">
          {content.coverSheetHidden && (
            <div className="cover-sheet-render-hidden-banner">
              This cover sheet is hidden from viewers.
            </div>
          )}

          <header className="cover-sheet-render-header">
            {photoObjectUrl ? (
              <div className="cover-sheet-render-photo">
                <img src={photoObjectUrl} alt="" />
                <div className="cover-sheet-render-photo-actions">
                  <button
                    type="button"
                    className="cover-sheet-photo-action"
                    onClick={handlePhotoButtonClick}
                    disabled={isUploadingPhoto || isRemovingPhoto}
                  >
                    {isUploadingPhoto ? 'Uploading…' : 'Change'}
                  </button>
                  <button
                    type="button"
                    className="cover-sheet-photo-action cover-sheet-photo-action-danger"
                    onClick={() => setConfirmRemove(true)}
                    disabled={isUploadingPhoto || isRemovingPhoto}
                  >
                    {isRemovingPhoto ? 'Removing…' : 'Remove'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="cover-sheet-render-photo-upload"
                onClick={handlePhotoButtonClick}
                disabled={isUploadingPhoto}
                title="Upload a JPEG or PNG, up to 10MB"
              >
                <span className="cover-sheet-render-photo-upload-plus" aria-hidden="true">+</span>
                <span className="cover-sheet-render-photo-upload-label">
                  {isUploadingPhoto ? 'Uploading…' : 'Add photo'}
                </span>
                <span className="cover-sheet-render-photo-upload-hint">JPG or PNG · up to 10MB</span>
              </button>
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png"
              onChange={handlePhotoFileChange}
              style={{ display: 'none' }}
            />
            <div className="cover-sheet-render-headlines">
              <h1 className="cover-sheet-render-address">
                {content.propertyAddress || '—'}
              </h1>
              {(agent.name || agent.role) && (
                <div className="cover-sheet-render-agent-name">
                  <strong>{agent.name || ''}</strong>
                  {agent.name && agent.role ? ', ' : ''}
                  {agent.role || ''}
                </div>
              )}
              {agent.brokerage && (
                <div className="cover-sheet-render-agent-brokerage">{agent.brokerage}</div>
              )}
              {contactLine && (
                <div className="cover-sheet-render-agent-contact">{contactLine}</div>
              )}
            </div>
          </header>

          <hr className="cover-sheet-render-divider" />

          <section className="cover-sheet-render-docs">
            <h2 className="cover-sheet-render-section-title">Documents in this package</h2>
            {content.documents && content.documents.length > 0 ? (
              <table className="cover-sheet-render-table">
                <thead>
                  <tr>
                    <th className="col-num">#</th>
                    <th className="col-name">Document</th>
                    <th className="col-pages">Pages</th>
                  </tr>
                </thead>
                <tbody>
                  {content.documents.map((d, i) => (
                    <tr key={`${d.name}-${i}`}>
                      <td className="col-num">{i + 1}</td>
                      <td className="col-name">{d.name}</td>
                      <td className="col-pages">{d.pageCount != null ? d.pageCount : '—'}</td>
                    </tr>
                  ))}
                  <tr className="cover-sheet-render-table-total">
                    <td className="col-num" />
                    <td className="col-name"><strong>Total</strong></td>
                    <td className="col-pages"><strong>{content.totalPageCount}</strong></td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <p className="cover-sheet-render-empty">No documents in this folder yet.</p>
            )}
          </section>

          <section className="cover-sheet-render-instructions">
            <h2 className="cover-sheet-render-section-title">Instructions</h2>
            {content.instructions ? (
              <div className="cover-sheet-render-instructions-box">
                {content.instructions.split(/\r?\n/).map((line, i) => (
                  <div key={i}>{line || ' '}</div>
                ))}
              </div>
            ) : (
              <div className="cover-sheet-render-instructions-empty">No instructions provided.</div>
            )}
          </section>

          {content.generatedAtDisplay && (
            <footer className="cover-sheet-render-footer">
              Last updated {content.generatedAtDisplay}
            </footer>
          )}
        </article>
      ) : (
        <div className="cover-sheet-preview-loading-card">No preview available.</div>
      )}

      {notification.show && (
        <div className={`cover-sheet-toast cover-sheet-toast-${notification.type}`} role="status">
          {notification.message}
        </div>
      )}

      {confirmRemove && (
        <div className="cover-sheet-confirm-overlay" onClick={() => setConfirmRemove(false)}>
          <div className="cover-sheet-confirm-card" onClick={(e) => e.stopPropagation()}>
            <h3>Remove cover sheet photo?</h3>
            <p>The cover sheet will render without a property photo. You can upload a new one anytime.</p>
            <div className="cover-sheet-confirm-actions">
              <button type="button" className="cover-sheet-confirm-cancel" onClick={() => setConfirmRemove(false)}>Cancel</button>
              <button type="button" className="cover-sheet-confirm-danger" onClick={handleRemovePhotoConfirmed}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoverSheetPreviewPage;
